/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect, useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { Field, Form, FormRenderProps } from 'react-final-form';
import { ActionButton, BrandColors, Header, InputContainerError, Modal, Text } from '@blend/enterprise-components';

import { useDispatch } from 'react-redux';
import { fonts } from '@blend/consumer-components';
import { AxiosError } from 'axios';
import { notaryAPI } from '../../../api/notary';
import { addNotifications } from '../../../actions/pageActions';
import { NotaryAPI } from '../../../api/notary/types';

import {
  DEFAULT_LOCAL_TIME,
  getDropdownInitialTimeZone,
  getScheduledTimeConfig,
  notaryEmailInputConfig,
  notaryNameInputConfig,
  scheduleDateInputConfig,
  scheduleTimeZoneConfig,
  searchNotaryInputConfig,
} from './inputConfigs';
import { NotarySearchResult } from './NotarySearchResult';
import { ModalContent } from '@components/common/enterprise/ModalContent/ModalContent';
import { formatIsoDate, formatScheduleTimeAsHHMMSS } from '@utils/formatting';
import { SearchFunction } from '@components/TypeaheadSearch/types';

const styles = StyleSheet.create({
  wrapper: {
    ...fonts.Roboto,
  },
  sectionHeader: {
    marginTop: 24,
    marginBottom: 8,
  },
  footerBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: 28,
    borderTop: `1px solid ${BrandColors.CLOUD_50}`,
    marginTop: 36,
  },
  rowSingle: {
    ':not(:last-child)': {
      marginBottom: 8,
    },
    display: 'flex',
    justifyContent: 'space-between',
  },
  descriptionText: {
    marginBottom: 16,
  },
  submitWrapper: {
    marginLeft: 8,
  },
});

export namespace RescheduleModal {
  export interface Props {
    isAdmin: boolean;
    isOpen: boolean;
    scheduleDate: string;
    scheduleTime?: string;
    timeZone?: string;
    roomId: string;
    notaryId?: string;
    signers: string[];
    handleClose: () => void;
    preferredTimeZone?: string;
    preferredTimeSlots?: string[];
  }
}

interface RescheduleModalFormValues {
  date: string;
  localTime: string;
  timeZone: string;
  notaryName?: string;
  notaryEmail?: string;
  notaryId?: string;
  // a custom field to track if the localTime dropdown was touched
  _localTimeTouched?: boolean;
}

export const RescheduleModal: React.FC<RescheduleModal.Props> = ({
  isOpen,
  roomId,
  scheduleDate,
  scheduleTime,
  timeZone,
  handleClose,
  notaryId,
  isAdmin,
  preferredTimeSlots,
  preferredTimeZone,
}) => {
  const dispatch = useDispatch();
  const [notary, setNotary] = useState<NotaryAPI.UserResponse>();
  useEffect(() => {
    const fetchNotaryUser = async () => {
      if (!notaryId) {
        return;
      }
      let loadedUser;
      if (isAdmin) {
        loadedUser = await notaryAPI.users.getUserById(notaryId);
      } else {
        const currentUser = await notaryAPI.users.fetchCurrentUser();
        loadedUser = currentUser.id === notaryId ? currentUser : undefined;
      }
      setNotary(loadedUser);
    };
    fetchNotaryUser();
  }, [notaryId, isAdmin]);

  const showError = ({ content }: { content?: string }) => {
    dispatch(
      addNotifications([
        {
          type: 'error',
          iconType: 'error',
          shouldAutoRemove: true,
          content,
        },
      ]),
    );
  };
  const handleSubmitForm = async (formValues: RescheduleModalFormValues) => {
    try {
      await notaryAPI.rooms.scheduleCall(roomId, {
        localTime: formValues.localTime,
        timeZone: formValues.timeZone,
        notaryId: formValues.notaryId!,
      });
      handleClose();
    } catch (error) {
      showError({ content: (error as AxiosError<{ message?: string }>)?.response?.data?.message });
      handleClose();
    }
  };
  const headerText = 'Confirm Notarization Appointment';
  // note that server does not support short timezone (ex: "PDT"), the following get location_based timezone name (ex: America/Los_Angeles)
  const initialTimeZone = getDropdownInitialTimeZone(timeZone);
  const date = formatIsoDate(scheduleDate);
  const initialLocalTime = formatScheduleTimeAsHHMMSS(scheduleTime, timeZone) || DEFAULT_LOCAL_TIME;

  const searchApprovedNotary: SearchFunction<NotaryAPI.UserResponse> = async (query: string) => {
    const result = await notaryAPI.users.searchApprovedUser(query);
    return {
      items: result.users,
      totalCount: result.totalCount,
    };
  };

  const renderContent: React.FC<FormRenderProps<RescheduleModalFormValues>> = ({
    handleSubmit,
    submitting,
    form,
    values,
    errors,
    submitFailed,
  }) => {
    const handleSelectNotary = (selectedUser: NotaryAPI.UserResponse) => {
      form.change('notaryName', `${selectedUser.firstName} ${selectedUser.lastName}`);
      form.change('notaryEmail', selectedUser.email);
      form.change('notaryId', selectedUser.id);
    };

    const searchConfig = {
      ...searchNotaryInputConfig,
      customComponentProps: {
        name: 'NotarySearch',
        ResultComponent: NotarySearchResult,
        onResultClick: handleSelectNotary,
        placeholder: 'Search for notaries',
        searchFunction: searchApprovedNotary,
      },
    };
    const scheduleTimeSlotsConfig = getScheduledTimeConfig(
      scheduleDate,
      values.timeZone,
      initialLocalTime,
      preferredTimeZone,
      preferredTimeSlots,
      // when the dropdown is clicked, set the _localTimeTouched field to true
      // this is used to validate the localTime field and ensure the notary is not just going with the default time
      // we cannot use `touched` from react-final-form because it only marks it as touched on value change. If the user just clicks the dropdown without changing the value, it will not be marked as touched.
      () => form.change('_localTimeTouched', true),
    );

    return (
      <div className={css(styles.wrapper)}>
        <div className={css(styles.descriptionText)}>
          <Text fontSize="md" colorName="NEUTRAL_800">
            {isAdmin
              ? 'Select a time for and assign a notary to this notarization'
              : 'Select a time for this notarization'}
            . If the borrower has shared their preferences, they will be indicated below.
          </Text>
        </div>

        <div className={css(styles.sectionHeader)}>
          <Header size={5} colorName="NIGHT_90">
            Appointment information
          </Header>
        </div>
        <div className={css(styles.rowSingle)}>
          <Field {...scheduleDateInputConfig} />
        </div>
        <div className={css(styles.rowSingle)}>
          <Field {...scheduleTimeZoneConfig} />
          <Field {...scheduleTimeSlotsConfig} />
        </div>

        <div className={css(styles.sectionHeader)}>
          <Header size={5} colorName="NIGHT_90">
            Notary information
          </Header>
        </div>
        {isAdmin && (
          <>
            <div className={css(styles.rowSingle)}>
              <Field {...searchConfig} />
            </div>
            {submitFailed && errors?.notaryId && (
              <div data-it="search-input-error" className={css(styles.rowSingle)}>
                <InputContainerError errorMessage={errors?.notaryId} />
              </div>
            )}
          </>
        )}
        <div className={css(styles.rowSingle)}>
          <Field {...notaryNameInputConfig} />
        </div>
        <div className={css(styles.rowSingle)}>
          <Field {...notaryEmailInputConfig} />
        </div>

        {isAdmin && (
          <Text data-it="auto-populate-hint" fontSize="sm" colorName="NEUTRAL_500">
            These fields will populate when you type and select a search result above
          </Text>
        )}
        <div className={css(styles.footerBar)}>
          <div className={css(styles.submitWrapper)}>
            <ActionButton
              data-dd-privacy="allow"
              disabled={submitting}
              isLoading={submitting}
              data-it="RescheduleModal-submit"
              label="Confirm"
              onClick={handleSubmit}
            />
          </div>
        </div>
      </div>
    );
  };

  const initialValue = {
    date,
    localTime: initialLocalTime,
    timeZone: initialTimeZone,
    ...(notary
      ? { notaryId: notary.id, notaryName: `${notary.firstName} ${notary.lastName}`, notaryEmail: notary.email }
      : {}),
  };

  const validateForm = (values: { notaryId?: string }) => {
    const errors: { notaryId?: string } = {};
    if (!values.notaryId) {
      errors.notaryId = 'A notary must be assigned in order to confirm this notarization';
    }
    return errors;
  };

  return (
    <Modal data-it="RescheduleModal" contentLabel={headerText} handleClose={handleClose} isOpen={isOpen}>
      <ModalContent handleClose={handleClose} headerText={headerText}>
        <Form initialValues={initialValue} onSubmit={handleSubmitForm} render={renderContent} validate={validateForm} />
      </ModalContent>
    </Modal>
  );
};
